/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Promised Prepayment Modal Component
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Nikolay Sviridov
 * @version 1.36.1
 * @since 1.10.0
 */

import { useCallback, useEffect } from 'react';

import moment from 'moment';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { getIsFastActionPage } from '@/main/components/Table/utils/tableUtils';
import { openRoute } from '@/main/routes/constants/externalRoutes';
import { RoutePath } from '@/main/routes/constants/routesConstants';

import { makeSelectWidgetPersonalAccountNumber } from '../../../pages/PageDashboard/components/Widgets/WidgetPersonalAccount/widgetPersosnalAccountSelector';
import { EFormatData } from '../../../pages/PageDashboard/types/widgetTypes';
import { IRootReducer } from '../../../reducers/rootReducer';
import { stringFormatParams } from '../../../utils/stringFormat';
import PageLocale from '../../internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '../../internationalization/constants';
import { makeSelectPromisedPaymentTranslation } from '../../internationalization/intSelectors';
import { IPromisedPaymentTranslation } from '../../internationalization/types/modules/promisedPayments';
import { Loader } from '../../loader/components/Loader';
import {
    ModalPopupContainerStyled,
    ModalPopupControlContainer,
    ModalPopupHintStyled,
    ModalPopupTextStyled,
    ModalPopupTitleStyled,
} from '../../modal/components/styled/ModalStyled';
import ButtonServiceUserDenied from '../../role/components/ButtonServiceUserDenied';
import { promisedPaymentGetAmountAction, promisedPaymentSubmitAction } from '../promisedPaymentActions';
import {
    makeSelectPromisedPaymentAmount,
    makeSelectPromisedPaymentError,
    makeSelectPromisedPaymentLoading,
    makeSelectPromisedPaymentValidity,
} from '../promisedPaymentSelectors';
import { IPromisedPaymentGetAmountActionPayload } from '../promisedPaymentTypes';

export interface IPromisedPaymentModalProps {
    clientId: string;
    modalId: string;
}

export type TPromisedPaymentModalProps = IPromisedPaymentModalProps & IConnectedProps & IDispatchProps;

export const PromisedPaymentModal = (props: TPromisedPaymentModalProps) => {
    const {
        clientId,
        modalId,

        amount,
        error,
        isLoading,
        accountNumber,
        validity,

        promisedPaymentSubmit,
        promisedPaymentGetAmount,
    } = props;
    const translation = props.promisedPaymentTranslation;

    const isFastActionPage = getIsFastActionPage();
    
    const submitHandler = useCallback(() => {
        const onCloseModal = () => {
            openRoute(RoutePath.dashboard);
        }
        if (isFastActionPage) {
            promisedPaymentSubmit({ clientId, modalId, onCloseModal });
        } else {
            promisedPaymentSubmit({ clientId, modalId });
        }
    }, [isFastActionPage, promisedPaymentSubmit, clientId, modalId]);


    // Get possibility on init
    useEffect(() => {
        promisedPaymentGetAmount({ clientId, modalId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [promisedPaymentGetAmount, modalId]);

    return <ModalPopupContainerStyled
        data-component={'modal-popup'}
        data-name={'promised-payment-modal'}
    >

        <PageLocale lazy
                    fileNames={[
                        TranslationFileNamesEnum.promisedPayment,
                    ]}/>

        {
            isLoading &&
            <Loader showLoader={true}/>
        }
        <ModalPopupTitleStyled data-name='modal-popup-title'>
            {translation.modal.title}
        </ModalPopupTitleStyled>

        <ModalPopupTextStyled data-name='modal-popup-text'>
            {
                stringFormatParams(translation.modal.text, {
                    sum: amount,
                })
            }
        </ModalPopupTextStyled>

        {
            !error && translation.modal.hint &&
            <ModalPopupHintStyled data-name='modal-popup-hint'>
                {stringFormatParams(translation.modal.hint, {
                    id: accountNumber,
                    date: moment(validity).format(EFormatData.FullDateWithTime),
                })}
            </ModalPopupHintStyled>
        }

        <ModalPopupControlContainer>
            <ButtonServiceUserDenied
                data-name='close-button'
                disabled={!!error || !amount}
                kind="mediumNormal"
                onClick={submitHandler}>
                {translation.modal.buttonSubmit}
            </ButtonServiceUserDenied>
        </ModalPopupControlContainer>
    </ModalPopupContainerStyled>
};

interface IConnectedProps {
    accountNumber: string;
    amount: number;
    error: string;
    isLoading: boolean;
    promisedPaymentTranslation: IPromisedPaymentTranslation;
    validity: string;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    accountNumber: makeSelectWidgetPersonalAccountNumber(),
    amount: makeSelectPromisedPaymentAmount(),
    error: makeSelectPromisedPaymentError(),
    isLoading: makeSelectPromisedPaymentLoading(),
    promisedPaymentTranslation: makeSelectPromisedPaymentTranslation(),
    validity: makeSelectPromisedPaymentValidity(),
});

interface IDispatchProps {
    promisedPaymentGetAmount: (payload: IPromisedPaymentGetAmountActionPayload) => void;
    promisedPaymentSubmit: (payload: IPromisedPaymentGetAmountActionPayload) => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    promisedPaymentGetAmount: (payload) => dispatch(promisedPaymentGetAmountAction(payload)),
    promisedPaymentSubmit: (payload) => dispatch(promisedPaymentSubmitAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PromisedPaymentModal);
